import type { ExecutionProviderDescriptor } from '@crowdex/agent-contracts/execution';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { RemoteExecutionTarget } from '@/components/worktree-controls';
import { cloudApiPath, loadCloudLink } from '@/lib/cloud-link';

export function remoteExecutionTargets(
  providers: readonly ExecutionProviderDescriptor[],
  cloud = false,
): RemoteExecutionTarget[] {
  return providers.flatMap((provider) =>
    provider.profiles.map((profile) => ({
      cloud,
      providerId: provider.id,
      profileId: profile.id,
      label:
        providers.length === 1 && provider.profiles.length === 1
          ? provider.name
          : `${provider.name} — ${profile.name}`,
      ...((profile.description ?? provider.description)
        ? { description: profile.description ?? provider.description }
        : {}),
    })),
  );
}

/**
 * Where a conversation can continue remotely: the providers of the session's
 * server and, once the user linked it, those of Crowdex Cloud.
 */
export async function loadExecutionTargets(): Promise<RemoteExecutionTarget[]> {
  const providers = (cloud: boolean) =>
    apiFetch<ExecutionProviderDescriptor[]>(cloudApiPath(cloud, '/api/execution/providers'), {
      credentials: 'include',
      cache: 'no-store',
    }).catch(() => [] as ExecutionProviderDescriptor[]);
  const linked = (await loadCloudLink().catch(() => null))?.account != null;
  const [own, cloud] = await Promise.all([
    providers(false),
    linked ? providers(true) : Promise.resolve([]),
  ]);
  return [...remoteExecutionTargets(own), ...remoteExecutionTargets(cloud, true)];
}
